import React from "react";
import { Link } from "react-router-dom";
import "./AboutPage.css";
import ceoImg from "../assets/ceo emmad.jpeg";
import useRevealOnIntersect from "../hooks/useRevealOnIntersect";
import { useScrollToTopOnMount } from "../hooks/useScrollToTop";

export default function AboutPage() {
  useScrollToTopOnMount();
  useRevealOnIntersect(".reveal", { threshold: 0.2 });

  const partners = [
    { name: "TechNova", src: "/partners/technova.png" },
    { name: "Byteworks", src: "/partners/byteworks.png" },
    { name: "CloudMint", src: "/partners/cloudmint.png" },
    { name: "DeltaSecure", src: "/partners/deltasecure.png" },
    { name: "FiberGrid", src: "/partners/fibergrid.png" },
    { name: "VisionAI", src: "/partners/visionai.png" },
  ];

  const timeline = [
    {
      year: "2019",
      title: "Nexite is founded",
      text:
        "Started with a small team of engineers focused on practical, elegant solutions to real-world problems.",
    },
    {
      year: "2020",
      title: "First enterprise rollouts",
      text:
        "Deployed multi-site solutions blending security, networking, and resilient IT infrastructure.",
    },
    {
      year: "2022",
      title: "Scale and specialization",
      text:
        "Expanded TURN-KEY capabilities, refined delivery playbooks, and strengthened partner ecosystem.",
    },
    {
      year: "2024",
      title: "AI-driven acceleration",
      text:
        "Brought AI analytics into core offerings for visibility, speed, and smarter decisions.",
    },
    {
      year: "Today",
      title: "Future-focused",
      text:
        "Continuing to innovate with client-first design, robust engineering, and measurable outcomes.",
    },
  ];

  return (
    <main className="about-page">
      {/* Hero with animated blobs and subtle parallax accents */}
      <header className="ab-hero">
        <div className="ab-hero-bg" aria-hidden="true">
          <span className="blob b1" />
          <span className="blob b2" />
          <span className="blob b3" />
          <span className="grid-glow" />
        </div>

        <div className="ab-container">
          <h1 className="reveal" data-reveal="up" style={{ "--d": "60ms" }}>
            About Us
          </h1>
          <p className="subtitle reveal" data-reveal="up" style={{ "--d": "140ms" }}>
            We build elegant solutions with cutting‑edge technology to help your business thrive.
          </p>
          <div className="ab-hero-actions reveal" data-reveal="up" style={{ "--d": "220ms" }}>
            <Link to="/services" className="ab-btn ab-btn-primary ab-btn-3d">
              <span className="shine" aria-hidden="true" />
              Get Started
            </Link>
            <Link to="/about#history" className="ab-btn ab-btn-secondary">Our Journey</Link>
          </div>
        </div>

        <div className="ab-hero-accent" aria-hidden="true" />
      </header>

      {/* Who We Are + CEO */}
      <section className="ab-section" id="who-we-are">
        <div className="ab-container ab-split">
          <div className="ab-col">
            <h2 className="reveal" data-reveal="up">Innovation at Nexite</h2>
            <p className="lead reveal" data-reveal="up" style={{ "--d": "80ms" }}>Who We Are</p>
            <p className="body reveal" data-reveal="up" style={{ "--d": "140ms" }}>
              Considering transformation in a fully connected, data‑driven era, NEXITE focuses on delivering
              technological CUTTING‑EDGE solutions and specializes in TURN‑KEY services to navigate the
              complexities of the modern digital world. NEXITE builds smart, scalable designs tailored by
              blending advanced technology, strategic insights, and creative expertise.
            </p>
          </div>

          <div className="ab-col">
            <div className="ab-ceo hover-tilt reveal" data-reveal="left">
              <div className="ab-ceo-pic">
                {ceoImg ? (
                  <img
                    src={ceoImg}
                    alt="CEO portrait"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : null}
              </div>
              <div className="ab-ceo-text">
                <h3>Message from our CEO</h3>
                <p>
                  At Nexite, innovation starts with empathy understanding your challenges and crafting
                  elegant technology that delivers measurable outcomes. We combine deep engineering
                  with a relentless focus on usability, scale, and security.
                </p>
                <p className="ab-ceo-sign">
                  <strong>— Emmad Asif</strong>, Founder & CEO, Nexite
                </p>
              </div>
              <span className="card-shine" aria-hidden="true" />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision with animated borders */}
      <section className="ab-section ab-alt" id="mission-vision">
        <div className="ab-container">
          <h2 className="reveal" data-reveal="up">Our Mission & Vision</h2>
          <div className="ab-mv">
            <div className="ab-mv-card reveal" data-reveal="up" style={{ "--d": "80ms" }}>
              <h3>Mission</h3>
              <p>
                Deliver future‑ready, secure, and scalable solutions that empower organizations to
                move faster with confidence—turning complex problems into beautifully simple outcomes.
              </p>
              <span className="ring" aria-hidden="true" />
            </div>
            <div className="ab-mv-card reveal" data-reveal="up" style={{ "--d": "140ms" }}>
              <h3>Vision</h3>
              <p>
                Be the trusted technology partner that builds the intelligent, connected infrastructure
                of tomorrow—where performance, security, and experience come together seamlessly.
              </p>
              <span className="ring" aria-hidden="true" />
            </div>
          </div>
        </div>
      </section>

      {/* History Timeline with pulsing nodes and slide-in items */}
      <section className="ab-section" id="history">
        <div className="ab-container">
          <h2 className="reveal" data-reveal="up">Our History</h2>
          <div className="ab-timeline">
            {timeline.map((item, idx) => (
              <div
                className={`ab-timeline-item reveal`}
                data-reveal={idx % 2 === 0 ? "left" : "right"}
                style={{ "--d": `${80 + idx * 80}ms` }}
                key={item.year}
              >
                <div className="ab-tl-left">
                  <div className="ab-tl-year">{item.year}</div>
                </div>
                <div className="ab-tl-dot" aria-hidden="true" />
                <div className="ab-tl-right">
                  <h4>{item.title}</h4>
                  <p>{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnerships with grayscale-to-color, float and shimmer */}
      <section className="ab-section ab-alt" id="partnerships">
        <div className="ab-container">
          <h2 className="reveal" data-reveal="up">Partnerships</h2>
          <p className="subtitle reveal" data-reveal="up" style={{ "--d": "80ms" }}>
            We collaborate with forward‑thinking organizations to deliver excellence—at speed and scale.
          </p>
          <div className="ab-partners">
            {partners.map((p, i) => (
              <div className="ab-partner reveal" data-reveal="up" style={{ "--d": `${60 + i * 60}ms` }} key={p.name} title={p.name}>
                <div className="ab-partner-card hover-tilt">
                  <img
                    src={p.src}
                    alt={p.name}
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                  <span className="ab-partner-name">{p.name}</span>
                  <span className="card-shine" aria-hidden="true" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Info Strip */}

    </main>
  );
}